<template>
  <div
    id="icon-code-snippet"
    class="icon-code-snippet codeblock-tabbed margin-bottom-xl"
  >
    <div v-if="icon">
      <div
        class="tabs"
        role="tablist"
      >
        <button
          v-for="syntax in syntaxes"
          :id="`tab-${sluggifySyntax(syntax)}`"
          :key="syntax"
          class="tab size-sm"
          style="--tab-color: var(--fa-blue); letter-spacing: 1px; text-wrap: nowrap"
          type="button"
          role="tab"
          :aria-controls="`code-snippet-${sluggifySyntax(syntax)}`"
          :aria-selected="showing == sluggifySyntax(syntax) ? 'true' : 'false'"
          @click="showSnippet(sluggifySyntax(syntax))"
        >
          {{ syntax }}
        </button>
      </div>
    </div>

    <div>
      <div class="code-snippet-content codeblock-container">
        <icon-code-snippet-html
          :default-styles="defaultStyles"
          :has-custom-styling="hasCustomStyling"
          :icon="icon"
          :is-duotone="isDuotone"
          :showing="showing"
          :styling="styling"
          :translated-style="translatedStyle"
          :major-version="activeMajorVersion"
          @markCopied="markCopied"
        />

        <icon-code-snippet-web-awesome
          :default-styles="defaultStyles"
          :icon="icon"
          :is-duotone="isDuotone"
          :showing="showing"
          :styling="styling"
          :major-version="activeMajorVersion"
        />

        <icon-code-snippet-react
          :default-styles="defaultStyles"
          :has-custom-styling="hasCustomStyling"
          :icon="icon"
          :is-custom-icon="isCustomIcon"
          :showing="showing"
          :styling="styling"
          :translated-style="translatedStyle"
          :major-version="activeMajorVersion"
          @click="handleIntegrationClick('react')"
        />

        <icon-code-snippet-vue
          :default-styles="defaultStyles"
          :has-custom-styling="hasCustomStyling"
          :icon="icon"
          :is-custom-icon="isCustomIcon"
          :showing="showing"
          :styling="styling"
          :translated-style="translatedStyle"
          :major-version="activeMajorVersion"
          @click="handleIntegrationClick('vue')"
        />

        <icon-code-snippet-svg
          :active-version="activeVersion"
          :default-styles="defaultStyles"
          :icon="icon"
          :is-custom-icon="isCustomIcon"
          :has-custom-styling="hasCustomStyling"
          :kit-version="kitVersion"
          :showing="showing"
          :styling="styling"
          :major-version="activeMajorVersion"
          :viggo="viggo"
          @copySvg="copySvg"
          @svg-format-change="(value) => $emit('svg-format-change', value)"
        />
      </div>
    </div>
  </div>
</template>

<script>
  const JUST_COPIED_TIMEOUT = 1000

  // width threshold to determine if a custom icon should be considered "wide"
  const WIDE_ICON_WIDTH_THRESHOLD = 640

  import IconCodeSnippetHtml from './IconCodeSnippetHtml'
  import IconCodeSnippetWebAwesome from './IconCodeSnippetWebAwesome'
  import IconCodeSnippetReact from './IconCodeSnippetReact'
  import IconCodeSnippetSvg from './IconCodeSnippetSvg'
  import IconCodeSnippetVue from './IconCodeSnippetVue'
  import isEqual from 'lodash/isEqual'
  import sluggify from 'app/utils/sluggify'
  import { DocsMixin, EventTrackingMixin } from 'app/mixins'

  export default {
    name: 'IconCodeSnippet',

    components: {
      IconCodeSnippetHtml,
      IconCodeSnippetWebAwesome,
      IconCodeSnippetReact,
      IconCodeSnippetSvg,
      IconCodeSnippetVue
    },

    mixins: [DocsMixin, EventTrackingMixin],

    provide() {
      return {
        isWideCustomIcon: () => this.isWideCustomIcon
      }
    },

    props: {
      canDownloadIcon: {
        type: Boolean,
        required: true
      },
      defaultStyles: {
        type: Object
      },
      icon: {
        type: Object
      },
      iconAbstract: {
        type: Object
      },
      isCustomIcon: {
        type: Boolean
      },
      isDuotone: {
        type: Boolean
      },
      isUsingModal: {
        type: Boolean,
        required: true
      },
      kitVersion: {
        type: Number
      },
      showing: {
        type: String
      },
      styling: {
        type: Object
      },
      isHidingSvg: {
        type: Boolean,
        default: false
      },
      viggo: {
        validator(value) {
          return !!value
        }
      }
    },

    data() {
      return {
        copyIcon: 'fas fa-copy',
        svgSource: '',
        justCopied: ''
      }
    },

    computed: {
      activeVersion() {
        return `v${this.$releases.active.version}`
      },

      hasCustomStyling() {
        return !isEqual(this.defaultStyles, this.styling)
      },

      id() {
        return this.icon?.name
      },

      isShowingWebAwesomeSyntax() {
        return this.activeMajorVersion > 6 && !this.isCustomIcon
      },

      isWideCustomIcon() {
        return this.activeMajorVersion > 6 && this.isCustomIcon && this.iconAbstract?.width > WIDE_ICON_WIDTH_THRESHOLD
      },

      showSvgSyntax() {
        return !this.isHidingSvg
      },

      syntaxes() {
        const syntaxList = ['HTML']

        if (this.isShowingWebAwesomeSyntax) {
          syntaxList.push('WEB AWESOME')
        }

        if (this.showSvgSyntax) {
          syntaxList.push('SVG')
        }

        syntaxList.push('REACT', 'VUE')

        return syntaxList
      },

      translatedStyle() {
        if (this.activeMajorVersion === 5) {
          switch (this.styling.family) {
            case 'brands':
              return 'fab'
            case 'kit':
              return 'fak'
            default:
              return `fa${this.styling.style.charAt(0)}`
          }
        } else {
          switch (this.styling.family) {
            case 'classic':
              return `fa-${this.styling.style}`

            case 'brands':
            case 'kit':
            case 'kit-duotone':
              return `fa-${this.styling.family}`

            default:
              return `fa-${this.styling.family} fa-${this.styling.style}`
          }
        }
      },

      activeMajorVersion() {
        return this.$releases.active.versionParts.major
      },

      majorVersion() {
        return this.kitVersion ? this.kitVersion : this.activeMajorVersion
      }
    },

    mounted() {
      // Load saved snippet preference from localStorage
      const savedSnippet = localStorage.getItem('fa-icon-snippet-preference')
      const isSavedSnippetValid = this.syntaxes.some((syntax) => this.sluggifySyntax(syntax) === savedSnippet)

      // Only use saved preference if it's currently available and user has access
      if (savedSnippet && isSavedSnippetValid) {
        // Don't auto-show SVG if user doesn't have access to prevent opening upsell modal
        if (savedSnippet === 'svg' && !this.canDownloadIcon) {
          // Default to HTML instead
          this.$emit('show', 'html')
        } else {
          this.$emit('show', savedSnippet)
        }
      }
    },

    methods: {
      handleIntegrationClick(integration) {
        const docsPath = this.getDocsPath(`/web/use-with/${integration}`, { version: this.majorVersion })
        const route = this.$router.resolve(docsPath)
        window.open(route.href, '_blank')
      },

      markCopied(what) {
        const oldCopyIcon = this.copyIcon

        this.copyIcon = 'fas fa-check'

        this.justCopied = what
        setTimeout(() => {
          this.copyIcon = oldCopyIcon
          this.justCopied = null
        }, JUST_COPIED_TIMEOUT)

        this.$posthog.capture(`icon_code_snippet_${what}_copy_click`, {
          context: `${this.iconModalContext(this.isUsingModal)}`,
          icon_name: this.icon.name,
          icon_style: this.styling.style,
          icon_family: this.styling.family
        })
      },

      showSnippet(snippet) {
        // Save the user's preference to localStorage
        localStorage.setItem('fa-icon-snippet-preference', snippet)

        if (!this.canDownloadIcon && snippet === 'svg') {
          localStorage.setItem('fa-icon-snippet-preference', 'html')
        }

        this.$emit('show', snippet)

        this.$posthog.capture(`icon_code_snippet_${snippet}_view`, {
          context: `${this.iconModalContext(this.isUsingModal)}`,
          icon_name: this.icon.name,
          icon_style: this.styling.style,
          icon_family: this.styling.family
        })
      },

      sluggifySyntax(syntax) {
        return sluggify(syntax)
      },

      async copySvg() {
        this.$emit('getSelectedAction', 'Downloading this Icon')

        if (this.canDownloadIcon) {
          this.$clipboard(this.svgSource)
          this.markCopied('svg')
        } else {
          this.$emit('openProIconModal')
          this.purchaseFlowEvent('icon_code_snippet_svg_copy_click', {
            context: `${this.iconModalContext(this.isUsingModal)}`,
            icon_name: this.icon.name,
            icon_style: this.styling.style,
            icon_family: this.styling.family
          })
        }
      }
    }
  }
</script>
